import { useCallback, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';

export function useSectionBackgroundLayout({ enabled }: { enabled: boolean }) {
  const [surfaceElement, setSurfaceElement] = useState<HTMLDivElement | null>(null);
  const [sectionHeight, setSectionHeight] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  const surfaceRef = useCallback((node: HTMLDivElement | null) => {
    setSurfaceElement((previousNode) => (previousNode === node ? previousNode : node));
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      setSectionHeight(0);
      return;
    }

    const view = surfaceElement?.ownerDocument.defaultView;
    if (!surfaceElement || !view) {
      return;
    }

    const update = () => {
      const height = Math.round(surfaceElement.getBoundingClientRect().height);
      setSectionHeight((prev) => (prev === height ? prev : height));
    };

    const scheduleUpdate = () => {
      if (animationFrameRef.current !== null) {
        view.cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = view.requestAnimationFrame(() => {
        animationFrameRef.current = null;
        update();
      });
    };

    update();

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(surfaceElement);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameRef.current !== null) {
        view.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [enabled, surfaceElement]);

  return { surfaceRef, sectionHeight };
}
